import React, { useState } from 'react';
import { X, Loader2, Plus, Sparkles } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { createTemplate } from '../../services/overlayApi';
import { useNavigate } from 'react-router-dom';

const ManageTemplateModal = ({ isOpen, onClose }) => {
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const navigate = useNavigate();

  const createTemplateMutation = useMutation({
    mutationFn: createTemplate,
    onSuccess: (data) => {
      // Navigate to builder with the template ID
      navigate(`/template/builder/${data.id}`);
      onClose();
    },
    onError: (error) => {
      console.error('Failed to create template:', error);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!templateName.trim()) {
      return;
    }

    // Create template with default page structure
    const defaultCanvasData = {
      width: 1080,
      height: 1080,
      fonts: [],
      pages: [
        {
          id: `page-${Date.now()}`,
          children: [],
          width: 1080,
          height: 1080,
          background: 'white',
        },
      ],
      unit: 'px',
      dpi: 72,
    };

    createTemplateMutation.mutate({
      name: templateName,
      description: description || undefined,
      canvas_data: defaultCanvasData,
    });
  };

  const handleClose = () => {
    if (!createTemplateMutation.isPending) {
      setTemplateName('');
      setDescription('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-[#8E0DFF] to-[#2F86DC] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Create Template</h2>
            </div>
            <button
              onClick={handleClose}
              disabled={createTemplateMutation.isPending}
              className="text-white/80 hover:text-white transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-white/90 text-sm mt-2">
            Start building your custom template
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Template Name */}
          <div>
            <label htmlFor="templateName" className="block text-sm font-semibold text-gray-700 mb-2">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              id="templateName"
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Product Showcase"
              disabled={createTemplateMutation.isPending}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#8E0DFF] focus:border-transparent outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
              Description <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your template..."
              disabled={createTemplateMutation.isPending}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#8E0DFF] focus:border-transparent outline-none transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Error Message */}
          {createTemplateMutation.isError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">
                {createTemplateMutation.error?.message || 'Failed to create template. Please try again.'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={createTemplateMutation.isPending}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createTemplateMutation.isPending || !templateName.trim()}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#8E0DFF] to-[#2F86DC] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-[#8E0DFF]/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {createTemplateMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Create Template
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageTemplateModal;
